import React from "react";
import styles from "./Select.module.css";
import { getClassName } from "../../utils/className.js";
import SelectTooltip from "./SelectTooltip.jsx";

/**
 * @typedef {Object} SelectOption
 * @property {string | number} value - The unique value of the option.
 * @property {string} label - The display text of the option.
 * @property {boolean} [disabled] - Whether the option is disabled.
 */

/**
 * NativeSelect renders a standard HTML <select> element with custom styles.
 * Used when basic select appearance is supported, and no advanced select options (e.g. search, async, multi) are enabled.
 *
 * @component
 * @param {Object} props
 * @param {Array<SelectOption>} props.normalizedOptions - Array of normalized option objects.
 * @param {string | number} [props.value] - Selected option value.
 * @param {string} [props.placeholder] - Custom placeholder text.
 * @param {string} [props.label] - Optional header label text above the field.
 * @param {boolean} [props.disabled] - Whether the select component is disabled.
 * @param {boolean | string} [props.error] - If boolean/string, styles field as invalid. If string, displays it as helper error text.
 * @param {string} [props.helperText] - Optional helper/hint text displayed below the field.
 * @param {boolean} [props.fullWidth] - If true, takes 100% width of parent container.
 * @param {string} [props.className] - External class name to style the container.
 * @param {Object} [props.classes] - Custom classes for inner components.
 * @param {string} [props.classes.root] - Custom class for root container.
 * @param {string} [props.classes.label] - Custom class for label text.
 * @param {string} [props.classes.option] - Custom class for option list items.
 * @param {React.CSSProperties} [props.style] - Inline style for container element.
 * @param {string} [props.id] - Unique ID for the wrapper element.
 * @param {(value: string | number, option?: SelectOption) => void} [props.onChange] - Callback fired when option is selected.
 * @param {boolean} [props.simulateNativeOnChange] - If true, calls onChange with a simulated native event { target: { name, value } }.
 * @param {React.ComponentType | React.ReactElement} [props.arrowIcon] - Custom icon component or element to replace the default dropdown arrow.
 * @param {boolean} [props.floatingLabel] - If true, renders the label as a floating label that animates above the trigger when focused or a value is selected.
 * @param {string} [props.tooltip] - Tooltip text shown (with a short hover delay) when hovering over the select.
 * @param {boolean} [props.tooltipLight] - If true, (white background, dark text).
 */
const NativeSelect = ({
  normalizedOptions,
  value,
  placeholder = "Select...",
  label,
  disabled = false,
  error = false,
  helperText,
  fullWidth = false,
  className: externalClassName,
  classes = {},
  style,
  id,
  onChange,
  simulateNativeOnChange = false,
  arrowIcon,
  floatingLabel = false,
  tooltip,
  tooltipLight = false,
  ...rest
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const selectRef = React.useRef(null);
  const selectedOption = normalizedOptions.find((opt) => opt.value === value) || null;

  const containerClassName = getClassName(
    styles.container,
    "select-container",
    {
      [styles.fullWidth]: fullWidth,
      "select-full-width": fullWidth,
      [styles.containerFloatingLabel]: !!floatingLabel,
    },
    classes.root,
    externalClassName,
  );

  const hasValue = value !== undefined && value !== null && value !== "";
  const isFloating = isFocused || hasValue;

  const labelClassName = getClassName(
    floatingLabel ? styles.labelFloating : styles.label,
    floatingLabel ? "select-label-floating" : "select-label",
    {
      [styles.labelError]: !floatingLabel && error,
      "select-label-error": !floatingLabel && error,
      [styles.labelFloatingActive]: !!floatingLabel && isFloating,
      "select-label-floating-active": !!floatingLabel && isFloating,
      [styles.labelFloatingError]: !!floatingLabel && error,
      "select-label-floating-error": !!floatingLabel && error,
      [styles.labelFloatingDisabled]: !!floatingLabel && disabled,
      "select-label-floating-disabled": !!floatingLabel && disabled,
    },
    classes.label,
  );

  const isErrorText = typeof error === "string" ? error : null;
  const bottomText = isErrorText || helperText;

  const handleNativeChange = (e) => {
    const val = e.target.value;
    const opt = normalizedOptions.find((o) => String(o.value) === val);
    if (simulateNativeOnChange) {
      onChange?.(e, opt);
    } else {
      onChange?.(val, opt);
    }
  };

  return (
    <div
      className={containerClassName}
      style={style}
      id={id}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...rest}
    >
      {label && !floatingLabel && <label className={labelClassName}>{label}</label>}
      <div className={getClassName(styles.selectWrapper, "select-wrapper")}>
        {label && floatingLabel && <label className={labelClassName}>{label}</label>}
        {label && floatingLabel && <span className={styles.labelGhost} aria-hidden="true">{label}</span>}
        <select
          ref={selectRef}
          className={getClassName(styles.nativeSelect, "select-native-select")}
          disabled={disabled}
          value={value !== undefined && value !== null ? String(value) : ""}
          onChange={handleNativeChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          <button type="button">
            <selectedoption className={getClassName(styles.selectionText, "select-selection-text")}>
              {selectedOption ? selectedOption.label : ((floatingLabel && !isFloating) ? "" : placeholder)}
            </selectedoption>
          </button>
          <div>
            {normalizedOptions.map((opt) => (
              <option
                key={opt.value}
                value={String(opt.value)}
                disabled={opt.disabled}
                className={getClassName(styles.option, "select-option")}
              >
                {opt.label}
              </option>
            ))}
          </div>
        </select>
        {(() => {
          if (!arrowIcon) {
            return (
              <svg
                className={getClassName(styles.arrowIcon, "select-arrow-icon")}
                viewBox="0 0 12 7"
                aria-hidden="true"
                style={{ pointerEvents: "none" }}
              >
                <path d="M1 0 L6 5 L11 0 L12 1 L6 7 L0 1 Z" />
              </svg>
            );
          }

          const isComponent =
            typeof arrowIcon === "function" ||
            (typeof arrowIcon === "object" &&
              arrowIcon &&
              arrowIcon.$$typeof &&
              !React.isValidElement(arrowIcon));

          const element = isComponent ? React.createElement(arrowIcon) : arrowIcon;

          if (React.isValidElement(element)) {
            return React.cloneElement(element, {
              className: getClassName(
                styles.arrowIcon,
                "select-arrow-icon",
                element.props.className
              ),
              style: {
                pointerEvents: "none",
                ...element.props.style,
              },
            });
          }

          return (
            <div
              className={getClassName(styles.arrowIcon, "select-arrow-icon")}
              style={{ pointerEvents: "none" }}
            >
              {element}
            </div>
          );
        })()}
        {floatingLabel && (
          <fieldset className={getClassName(styles.notchedOutline, "select-notched-outline")} aria-hidden="true">
            <legend className={getClassName(styles.notchedOutlineLegend, "select-notched-outline-legend", { [styles.notchedOutlineLegendActive]: isFloating })}>
              <span>{label}</span>
            </legend>
          </fieldset>
        )}

        <SelectTooltip text={tooltip} light={tooltipLight} anchorRef={selectRef} />
      </div>

      {bottomText && (
        <div
          className={getClassName(
            styles.helperText,
            "select-helper-text",
            {
              [styles.errorText]: isErrorText,
              "select-error-text": isErrorText,
            }
          )}
        >
          {bottomText}
        </div>
      )}
    </div>
  );
};

export default NativeSelect;
